// Seen marks (v9.8.0): which handouts a person has opened. Pure.
//
// ONE DOCUMENT PER PERSON (`handoutSeen/<uid>`, access `member-own`): a map
// handoutId -> seenAt, travelling as text. Never a map inside a shared
// document: every player marking at once would rewrite the same record, and
// one oversized shared document jams every write after it (gotcha 12). It
// is pruned to the handouts in the owner's feed on every write, so it is
// bounded by what they can see.
//
// A mark counts only if it is NOT OLDER than the reveal: a handout taken
// back and shown again is new again, without anyone having to clear marks.
import { TO_ALL, TO_PLAYERS } from '../chat/types';
import type { Handout } from './types';

/** handoutId -> when its owner opened it. */
export type SeenMarks = Readonly<Record<string, number>>;

/** Shown to somebody, and opened since it was. */
export function isSeen(h: Handout, marks: SeenMarks): boolean {
  const at = marks[h.id] ?? 0;
  return at > 0 && at >= h.revealedAt;
}

/**
 * The marks with `h` opened now; the SAME object when it already is, so nothing is written.
 *
 * NEVER STAMPED BEFORE THE REVEAL IT ANSWERS: `revealedAt` is the GM's clock and `now` the
 * opener's, and an opener whose clock ran behind left a mark that did not count.
 */
export function markSeen(marks: SeenMarks, h: Handout, now: number): SeenMarks {
  return isSeen(h, marks) ? marks : { ...marks, [h.id]: Math.max(now, h.revealedAt) };
}

/** Only the marks for handouts still in the feed. */
export function pruneSeen(marks: SeenMarks, handouts: readonly Handout[]): SeenMarks {
  const ids = new Set(handouts.map((h) => h.id));
  return Object.fromEntries(Object.entries(marks).filter(([id]) => ids.has(id)));
}

/** What is new in a feed: shown (a draft is the GM's own) and not opened. */
export function unreadHandouts(handouts: readonly Handout[], marks: SeenMarks): Handout[] {
  return handouts.filter((h) => h.audience.length > 0 && !isSeen(h, marks));
}

/** The document as it travels: `uid` is the owner the rules bind, the map is text. */
export function seenDoc(uid: string, marks: SeenMarks, now: number): { uid: string; data: string; updatedAt: number } {
  return { uid, data: JSON.stringify(marks), updatedAt: now };
}

const isMark = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

/** The stored map read back; anything that is not one reads as nothing seen. */
export function readSeenMarks(raw: unknown): SeenMarks {
  if (typeof raw !== 'string' || !raw) return {};
  try {
    const stored: unknown = JSON.parse(raw);
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return Object.fromEntries(Object.entries(stored).filter((e): e is [string, number] => isMark(e[1])));
  } catch {
    return {};
  }
}

interface Known {
  uid: string;
  isGm: boolean;
}

/** Who a handout was shown to, among the people the session knows: players only, the GM showed it. */
function audiencePeople<P extends Known>(h: Handout, people: readonly P[]): P[] {
  const players = people.filter((p) => !p.isGm);
  if (h.audience.includes(TO_ALL) || h.audience.includes(TO_PLAYERS)) return players;
  return players.filter((p) => h.audience.includes(p.uid));
}

/** "Seen by 2 of 3": how many of those it was shown to have opened it. */
export function seenCount(
  h: Handout,
  byUid: Readonly<Record<string, SeenMarks>>,
  people: readonly Known[],
): { seen: number; of: number } {
  const audience = audiencePeople(h, people);
  return { seen: audience.filter((p) => isSeen(h, byUid[p.uid] ?? {})).length, of: audience.length };
}
